import { isEditButtonAvailableForState, isEditableExperiment } from 'modules/experiments/utils';
import {
  getCampaignEditorLinkWithExperimentTakeover,
  getExperimentMonitorLink,
} from 'utils/appRoutes';
import noop from 'utils/noop';
import { ExperimentTakeoverSource } from '../constants';
import {
  GetExperimentLinkAttributes,
  OutsideEditorPageExperimentLinkAttributes,
  TypeCampaign,
} from '../types';

const FALLBACK_ATTRIBUTES = { href: '#', onClick: noop };

export const isOutsideCampaignEditor = (
  args: GetExperimentLinkAttributes,
): args is OutsideEditorPageExperimentLinkAttributes => 'campaign' in args;

export const getCampaignType = (campaign: TypeCampaign) => {
  let campaignType;
  if ('campaignTypeDefinition' in campaign) {
    campaignType = campaign.campaignTypeDefinition;
  } else if ('type' in campaign) {
    campaignType = campaign.type;
  }

  return campaignType;
};

export const routeToExperimentModule = (
  args: GetExperimentLinkAttributes,
  openExperimentTakeover: (experimentId: number) => void,
): React.AnchorHTMLAttributes<HTMLAnchorElement> => {
  const { source, experiment, onClickCallback = noop } = args;
  const { id: experimentId, state: experimentState } = experiment || {};

  // If the experiment object hasn't loaded.
  // we simply fallback
  if (!experimentId || !experimentState) return FALLBACK_ATTRIBUTES;

  const isExperimentEditable = isEditableExperiment(experimentState);
  /**
   * Inside the experiment module clicking on a running experiment should
   * take us to the respective experiment takeover.
   *
   * The experiment name rendered in the experiment table
   * is the only exception to this rule that is why
   * we don't see EXPERIMENT_NAME_INDEX in this list
   */
  const isExperimentModule = [
    ExperimentTakeoverSource.EXPERIMENT_ACTION_MENU_INDEX,
    ExperimentTakeoverSource.EXPERIMENT_ANALYTICS,
    ExperimentTakeoverSource.EXPERIMENT_INDEX_CHOOSE_CAMPAIGN_TAKEOVER,
  ].includes(source);
  const isInsideCampaignEditor = [
    ExperimentTakeoverSource.CAMPAIGN_EDITOR,
    ExperimentTakeoverSource.WORKFLOW_NODE_EDITOR,
  ].includes(source);
  const MONITOR_PAGE = {
    href: getExperimentMonitorLink(experimentId),
  };
  const OPEN_TAKEOVER = {
    onClick: () => {
      onClickCallback();
      openExperimentTakeover(experimentId);
    },
  };

  /**
   * If the link is already inside the campaign or journey
   * editor. Then all we want to do is to simply open
   * the experiment takeover
   */
  if (isInsideCampaignEditor) {
    if (!isExperimentEditable) return MONITOR_PAGE;

    return OPEN_TAKEOVER;
  }

  /**
   * If a link is outside the campaign or journey editor
   * then we will be provided with the campaign object
   * which will help us to re-route to the respective editors.
   */
  if (isOutsideCampaignEditor(args)) {
    const { campaign } = args;
    const campaignType = getCampaignType(campaign);
    const { id: campaignId } = campaign;

    // Note: Once EX-1482 is done. Start checking for campaignType
    // and route to the right campaign editor
    const REDIRECT_AND_OPEN_TAKEOVER = {
      href: getCampaignEditorLinkWithExperimentTakeover(campaignId, experimentId),
    };

    if (!campaignType) return FALLBACK_ATTRIBUTES;
    /**
     * Inside the experiment module we redirect a running experiment
     * to the takeover as well. This behavior is allowed only inside
     * our experiment module.
     */
    const isExperimentEditableInsideExperimentModule = isEditButtonAvailableForState(
      campaignType,
      experimentState,
    );

    switch (true) {
      case isExperimentModule && isExperimentEditableInsideExperimentModule:
      case !isExperimentModule && isExperimentEditable:
        return REDIRECT_AND_OPEN_TAKEOVER;

      default:
        return MONITOR_PAGE;
    }
  }

  return FALLBACK_ATTRIBUTES;
};

type ModifyUrlParam = {
  experimentId: number;
  urlParams: string;
  currentUrl: string;
};

export const addExperimentIdUrlParam = ({
  experimentId,
  urlParams,
  currentUrl,
}: ModifyUrlParam) => {
  // Append or modify the current url param
  const currentParams = new URLSearchParams(urlParams);
  currentParams.set('experimentId', experimentId.toString());

  // Create the new URL with the modified url params
  const newSearch = currentParams.toString();
  return `${currentUrl}?${newSearch}`;
};

export const removeExperimentIdUrlParam = ({
  urlParams,
  currentUrl,
}: Omit<ModifyUrlParam, 'experimentId'>) => {
  // Remove the 'experimentId' url param
  const currentParams = new URLSearchParams(urlParams);
  currentParams.delete('experimentId');

  // Create the new URL with the modified url params
  const newSearch = currentParams.toString();
  return newSearch.length ? `${currentUrl}?${newSearch}` : currentUrl;
};
